import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { getOffersBySubcategory } from '../services/offerService';
import { useAuth } from '../context/AuthContext';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArticleIcon from '@mui/icons-material/Article';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const StyledCard = styled(Card)(({ theme, expired }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  opacity: expired ? 0.7 : 1,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const OfferCard = ({ offer }) => {
  const isExpired = dayjs().isAfter(dayjs(offer.expirationDate));
  
  return (
    <StyledCard expired={isExpired}>
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {offer.title}
          </Typography>
          {isExpired && (
            <Chip 
              label="Expired" 
              size="small" 
              color="error" 
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {offer.description.length > 150 
            ? `${offer.description.substring(0, 150)}...` 
            : offer.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {isExpired 
              ? `Expired ${dayjs(offer.expirationDate).format('MMM D, YYYY')}`
              : `Expires ${dayjs(offer.expirationDate).format('MMM D, YYYY')}`
            }
          </Typography>
        </Box>

        {offer.originalPrice && offer.finalPrice && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
              ${offer.originalPrice}
            </Typography>
            <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
              ${offer.finalPrice}
            </Typography>
            {offer.discountPercentage && (
              <Chip 
                label={`${offer.discountPercentage}% OFF`} 
                size="small" 
                color="success" 
                variant="outlined"
              />
            )}
          </Box>
        )}

        {offer.urgencyText && (
          <Chip 
            label={offer.urgencyText} 
            size="small" 
            color="warning" 
            variant="outlined"
            sx={{ mb: 1 }}
          />
        )}

        {offer.features && offer.features.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              Features:
            </Typography>
            <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
              {offer.features.slice(0, 3).map((feature, index) => (
                <li key={index}>
                  <Typography variant="caption" color="text.secondary">
                    {feature}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}

        {offer.tags && offer.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {offer.tags.slice(0, 4).map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
            {offer.tags.length > 4 && (
              <Chip label={`+${offer.tags.length - 4}`} size="small" variant="outlined" />
            )}
          </Box>
        )}
      </CardContent>
      
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          component="a"
          href={offer.purchaseUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          disabled={isExpired}
          sx={{ mr: 1 }}
          size="small"
        >
          {offer.callToAction || 'Get Offer'}
        </Button>
        
        {offer.blogLink && (
          <Button
            component="a"
            href={offer.blogLink}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={<ArticleIcon />}
            size="small"
          >
            Read More
          </Button>
        )}
        
        <Button
          component={RouterLink}
          to={`/offers/${offer.slug}`}
          variant="text"
          size="small"
          sx={{ ml: 'auto' }}
        >
          Details
        </Button>
      </CardActions>
    </StyledCard>
  );
};

const SubcategoryOffers = () => {
  const { categoryId, subcategoryId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [subcategoryInfo, setSubcategoryInfo] = useState(null);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchOffers();
  }, [categoryId, subcategoryId, page, includeExpired, sortBy]);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const data = await getOffersBySubcategory(
        categoryId, 
        subcategoryId, 
        page, 
        12, // limit
        includeExpired
      );
      
      setOffers(data.offers);
      setTotalPages(data.totalPages);
      setCategoryInfo(data.category);
      setSubcategoryInfo(data.subcategory);
      setError(null);
    } catch (err) {
      setError('Failed to fetch offers. Please try again later.');
      console.error('Error fetching subcategory offers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleIncludeExpiredChange = (event) => {
    setIncludeExpired(event.target.checked);
    setPage(1);
  };

  if (loading && page === 1) {
    return (
      <DashboardLayout title="Loading...">
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={subcategoryInfo ? `${subcategoryInfo.name} Offers` : "Subcategory Offers"}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => navigate('/offers/by-category')}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Back to Categories
          </Button>
        </Box>

      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/offers" underline="hover">
            Offers
          </Link>
          <Link component={RouterLink} to="/offers/by-category" underline="hover">
            Categories
          </Link>
          {categoryInfo && (
            <Typography color="text.primary">
              {categoryInfo.name}
            </Typography>
          )}
          {subcategoryInfo && (
            <Typography color="text.primary" sx={{ fontWeight: 'bold' }}>
              {subcategoryInfo.name}
            </Typography>
          )}
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CategoryIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              {subcategoryInfo?.name || 'Offers'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              in {categoryInfo?.name || 'Category'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={handleSortChange}
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
                <MenuItem value="expiring">Expiring Soon</MenuItem>
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={includeExpired}
                  onChange={handleIncludeExpiredChange}
                  size="small"
                />
              }
              label="Include expired"
            />
          </Box>
          
          {isAuthenticated && (
            <Button
              component={RouterLink}
              to="/offers/new"
              variant="contained"
              startIcon={<LocalOfferIcon />}
            >
              Create Offer
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {offers.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LocalOfferIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No offers found in this subcategory
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {includeExpired 
              ? 'There are no offers available at the moment.'
              : 'Try including expired offers or check back later for new deals.'
            }
          </Typography>
          {isAuthenticated && (
            <Button
              component={RouterLink}
              to="/offers/new"
              variant="contained"
              startIcon={<LocalOfferIcon />}
            >
              Create First Offer
            </Button>
          )}
        </Box>
      )}

      {offers.length > 0 && (
        <>
          <Grid container spacing={3}>
            {offers.map((offer) => (
              <Grid item xs={12} sm={6} md={4} key={offer._id}>
                <OfferCard offer={offer} />
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Container>
    </DashboardLayout>
  );
};

export default SubcategoryOffers; 